import { ObjectId, View } from "@novorender/api";
import { ObjectDB } from "@novorender/data-js-api";

import { SelectionResolution } from "features/render/types";
import { telemetry } from "telemetry";
import { NodeType } from "types/misc";

import {
    getAncestorSearchDepth,
    getFilePathFromObjectPath,
    getNodeClass,
    getParentPath,
    NodeClass,
} from "./objectData";

export interface SelectionTarget {
    /** The main object ID (used for setMainObject) */
    mainObjectId: ObjectId;
    /** All object IDs to highlight (includes descendants for folder objects) */
    highlightIds: ObjectId[];
}

/** A node in the ancestor chain derived from an object's path. */
interface ChainNode {
    id: ObjectId;
    path: string;
    nodeClass: NodeClass | undefined;
    /** `NodeType` (Leaf/Internal), used to skip the descendant search for leaves. */
    type?: number;
    /** Pre-resolved descendant ids, if the metadata already carried them. */
    descendants?: readonly ObjectId[];
}

function single(objectId: ObjectId): SelectionTarget {
    return { mainObjectId: objectId, highlightIds: [objectId] };
}

function getObjectMeta(view: View, db: ObjectDB, objectId: ObjectId) {
    return view.data ? view.data.getObjectMetaData(objectId) : db.getObjectMetdata(objectId);
}

/**
 * The metadata shape we rely on for resolution. `view.data.getObjectMetaData` and
 * `db.getObjectMetdata` return slightly different types (the latter adds `save`/`loadMetaData`);
 * we only read `id`/`path`/`type`/`descendants`/`properties`, so the common base is sufficient.
 */
type ResolvedMeta = Awaited<ReturnType<typeof getObjectMeta>>;

/**
 * Resolves the selection target based on the selection resolution setting.
 *
 * When the clicked object carries `Procore/NodeClass`, selection resolves by class:
 * - FirstObject → the shallowest `object` in the clicked node's full ancestor chain (never a
 *   container/file).
 * - LastObject → the deepest `object`, skipping `container` and `fragment` (a click on a
 *   fragment / propertyless leaf rolls up to its nearest object).
 *
 * When `Procore/NodeClass` is absent (legacy / previously processed scenes), it falls back to
 * the previous behavior with no regression (FirstObject one level up; LastObject = clicked).
 *
 * @returns The selection target with main object ID and all IDs to highlight.
 */
export async function resolveSelectionTarget({
    objectId,
    db,
    view,
    resolution,
    abortSignal,
    classAware,
}: {
    objectId: ObjectId;
    db: ObjectDB;
    view: View;
    resolution: SelectionResolution;
    abortSignal: AbortSignal;
    /**
     * Whether class-aware (NodeClass) resolution is enabled. The value is decided by the
     * caller — ultimately by a host feature flag (e.g. LaunchDarkly evaluated by the embedder
     * and passed in via scene config), not by this module. When `false`, none of the NodeClass
     * logic runs and selection resolves exactly as it did before NodeClass existed.
     */
    classAware: boolean;
}): Promise<SelectionTarget> {
    const startedAt = performance.now();
    let result: SelectionTarget;
    let outcome: "resolved" | "legacy" | "error-fallback";

    try {
        // Flag off: behave exactly like the pre-NodeClass implementation. LastObject is the
        // clicked object (zero I/O); FirstObject does the legacy one-level-up path walk. No
        // NodeClass metadata is read.
        if (!classAware) {
            if (resolution === SelectionResolution.LastObject) {
                result = single(objectId);
            } else {
                const metadata = await getObjectMeta(view, db, objectId);
                result = await legacyResolveSelectionTarget({ objectId, metadata, db, resolution, abortSignal });
            }
            outcome = "legacy";
        } else {
            const metadata = await getObjectMeta(view, db, objectId);
            const nodeClass = getNodeClass(metadata.properties);
            // Detect NodeClass for this clicked node, not the whole scene: a federated scene can
            // contain files produced by different pipelines, so its files may differ.
            if (nodeClass === undefined) {
                result = await legacyResolveSelectionTarget({ objectId, metadata, db, resolution, abortSignal });
                outcome = "legacy";
            } else {
                result = await classAwareResolveSelectionTarget({
                    objectId,
                    metadata,
                    nodeClass,
                    db,
                    resolution,
                    abortSignal,
                });
                outcome = "resolved";
            }
        }
    } catch (error) {
        // An aborted signal means a newer pick superseded this one; the caller
        // discards the result after checking `abortSignal.aborted`. That is
        // expected control flow, not a failure — don't log it or report it as a
        // caught exception.
        if ((error as { name?: string } | undefined)?.name !== "AbortError") {
            console.warn("Error resolving selection target:", error);
            telemetry.trackCaughtException({
                source: "render.selectionResolution",
                category: "render",
                error,
            });
        }
        result = single(objectId);
        outcome = "error-fallback";
    }

    telemetry.trackEvent("render.selection_resolution", {
        "selection.mode": resolution,
        "selection.class_aware": classAware,
        "selection.outcome": outcome,
        "selection.target_changed": result.mainObjectId !== objectId,
        "selection.highlight_count": result.highlightIds.length,
        "selection.elapsed_ms": performance.now() - startedAt,
    });
    return result;
}

/**
 * NodeClass-aware resolution. Walks the ancestor chain (derived from the path) and stops at an
 * `object`, skipping `container` (incl. `file`) and `fragment` nodes.
 */
async function classAwareResolveSelectionTarget({
    objectId,
    metadata,
    nodeClass,
    db,
    resolution,
    abortSignal,
}: {
    objectId: ObjectId;
    metadata: ResolvedMeta;
    nodeClass: NodeClass;
    db: ObjectDB;
    resolution: SelectionResolution;
    abortSignal: AbortSignal;
}): Promise<SelectionTarget> {
    const clicked: ChainNode = {
        id: metadata.id ?? objectId,
        path: metadata.path,
        nodeClass,
        type: metadata.type,
        descendants: metadata.descendants,
    };

    // Perf (2): a click directly on an `object` is already the deepest object (the chain only
    // extends upward from the click), so LastObject needs no ancestor walk.
    if (resolution === SelectionResolution.LastObject && nodeClass === NodeClass.Object) {
        return {
            mainObjectId: clicked.id,
            highlightIds: await collectTargetAndDescendants({ target: clicked, db, abortSignal }),
        };
    }

    // Perf (5): a click directly on an `object` with FirstObject resolution. The clicked object is
    // the shallowest object unless another object exists above it in the full ancestor chain.
    // Fetch the ancestor chain and the clicked object's descendants CONCURRENTLY: in the common
    // non-nested case the clicked object is the target, so its descendants are exactly the
    // highlight set and the extra ancestor round-trip no longer adds to latency (matching
    // LastObject). Only nested objects pay for a second descendant search.
    if (resolution === SelectionResolution.FirstObject && nodeClass === NodeClass.Object) {
        const [chain, clickedDescendants] = await Promise.all([
            buildAncestorChain({ clicked, db, abortSignal }),
            collectTargetAndDescendants({ target: clicked, db, abortSignal }),
        ]);

        // Shallowest object in the chain (deepest-first), defaulting to the clicked object itself.
        let target = clicked;
        for (const node of chain) {
            if (node.nodeClass === NodeClass.Object) {
                target = node;
            }
        }

        if (target.id === clicked.id) {
            return { mainObjectId: clicked.id, highlightIds: clickedDescendants };
        }

        return {
            mainObjectId: target.id,
            highlightIds: await collectTargetAndDescendants({ target, db, abortSignal }),
        };
    }

    // Chain ordered deepest-first: [clicked, ...ancestors up to the file].
    const chain = await buildAncestorChain({ clicked, db, abortSignal });

    let target: ChainNode | undefined;
    if (resolution === SelectionResolution.LastObject) {
        // Deepest object: first `object` walking up from the clicked node.
        target = chain.find((node) => node.nodeClass === NodeClass.Object);
    } else {
        // FirstObject is the shallowest `object` in the full ancestor chain. Containers and files
        // do not form a selection boundary: an object above an intervening container remains the
        // first object.
        for (const node of chain) {
            if (node.nodeClass === NodeClass.Object) {
                target = node;
            }
        }
    }

    // No object anywhere in the chain: a model (or federated layer) with only containers/fragments,
    // or a bare-container click. Fall back to legacy resolution so FirstObject rolls up to the
    // parent + descendants like before NodeClass (LastObject already matches legacy's single()),
    // instead of selecting only the clicked node. NodeClass only ever upgrades resolution when it
    // actually finds an object; on any path with no object we behave exactly like pre-NodeClass.
    if (!target) {
        return await legacyResolveSelectionTarget({ objectId, metadata, db, resolution, abortSignal });
    }

    const highlightIds = await collectTargetAndDescendants({ target, db, abortSignal });
    return { mainObjectId: target.id, highlightIds };
}

/**
 * Builds the ancestor chain for an object from its path, deepest-first. Each node carries its
 * `Procore/NodeClass`. Reuses the same negative-`descentDepth` search the pick handler already
 * uses for the property stamp — no new metadata/search API.
 */
async function buildAncestorChain({
    clicked,
    db,
    abortSignal,
}: {
    clicked: ChainNode;
    db: ObjectDB;
    abortSignal: AbortSignal;
}): Promise<ChainNode[]> {
    const chain: ChainNode[] = [clicked];

    const path = clicked.path;

    // Top-level node: no parent, so no ancestors to walk — skip the (empty) ancestor search.
    if (!getParentPath(path)) {
        return chain;
    }

    // Fetch the complete ancestor chain in one search. Search results implement
    // HierarcicalObjectReference, so NodeClass properties must be read through
    // loadMetaData() rather than assumed to be present on the reference itself.
    // Dedupe defensively and skip the clicked node itself.
    const seen = new Set<ObjectId>([clicked.id]);
    const iter = db.search({ full: true, parentPath: path, descentDepth: getAncestorSearchDepth(path) }, abortSignal);
    for await (const ref of iter) {
        if (seen.has(ref.id) || ref.path === path) {
            continue;
        }
        const metadata = await ref.loadMetaData();
        seen.add(ref.id);
        chain.push({
            id: ref.id,
            path: ref.path,
            nodeClass: getNodeClass(metadata.properties),
            type: ref.type,
            descendants: ref.descendants,
        });
    }

    // Deepest-first so FirstObject (shallowest object) and LastObject (deepest object) read naturally.
    chain.sort((a, b) => b.path.length - a.path.length);
    return chain;
}

/**
 * Highlight set for a resolved object: the object itself plus all its descendants, so internal
 * object nodes (e.g. an object rolled up from a fragment) render their geometry.
 *
 * Perf (3): a `Leaf` object has no descendants (skip the search); a node that already carries a
 * populated `descendants` id list uses it directly (mirrors `search.ts`'s
 * `parentNode.descendants ?? db.descendants(...)`). Otherwise it falls back to a descendant search.
 */
async function collectTargetAndDescendants({
    target,
    db,
    abortSignal,
}: {
    target: ChainNode;
    db: ObjectDB;
    abortSignal: AbortSignal;
}): Promise<ObjectId[]> {
    if (target.type === NodeType.Leaf) {
        return [target.id];
    }

    if (target.descendants && target.descendants.length > 0) {
        return [target.id, ...target.descendants];
    }

    // Highlighting only needs ids, so omit `full` — loading full metadata for every descendant of a
    // large object is pure overhead here.
    const ids: ObjectId[] = [target.id];
    const iter = db.search({ parentPath: target.path }, abortSignal);
    for await (const obj of iter) {
        if (obj.id !== target.id) {
            ids.push(obj.id);
        }
    }

    return ids;
}

/**
 * Legacy (pre-NodeClass) resolution, preserved verbatim so older/mixed scenes keep working:
 * - LastObject: the clicked object exactly as today.
 * - FirstObject: the parent of the clicked object and all its descendants (one-level-up path walk).
 */
async function legacyResolveSelectionTarget({
    objectId,
    metadata,
    db,
    resolution,
    abortSignal,
}: {
    objectId: ObjectId;
    metadata: ResolvedMeta;
    db: ObjectDB;
    resolution: SelectionResolution;
    abortSignal: AbortSignal;
}): Promise<SelectionTarget> {
    // LastObject: return the original object (current behavior)
    if (resolution === SelectionResolution.LastObject) {
        return single(objectId);
    }

    // FirstObject: select the parent of the clicked object and all its descendants
    // Get the parent path
    const parentPath = getParentPath(metadata.path);
    const filePath = getFilePathFromObjectPath(parentPath);

    // If no parent path (we're at root), return original object.
    // If the parent is the file itself, the clicked object is a direct child of the
    // file, resolve to the clicked object itself instead.
    if (!parentPath || filePath === parentPath) {
        return single(objectId);
    }

    const fileExt = filePath?.split(".").at(-1)?.toLowerCase();

    if (fileExt === "rvt") {
        // RVT file structure is fixed, see BM-7989:
        //   Filename
        //   Category (e.g., Doors)
        //   Family (e.g., Single-Flush)
        //   Type (e.g., 36"x80")
        //   Instance (individual placed object)
        // First/Last object resolution should always select the same object.
        return single(objectId);
    }

    // Find the parent object
    let parentObjectId: ObjectId | undefined;
    const highlightIds: ObjectId[] = [];

    const findParentOfParent = async () => {
        // Search for the parent object itself
        const parentOfParentPath = getParentPath(parentPath);
        const parentSearchIter = db.search(
            {
                parentPath: parentOfParentPath,
                descentDepth: 0,
                full: true,
            },
            abortSignal,
        );

        for await (const obj of parentSearchIter) {
            if (obj.path === parentPath) {
                parentObjectId = obj.id;
                highlightIds.push(obj.id);
                break;
            }
        }
    };

    // Get all descendants of the parent (children, grandchildren, etc.)
    let firstDescendantObjectId: ObjectId | undefined;
    const findParentDescendants = async () => {
        const descendantsIter = db.search(
            {
                parentPath: parentPath,
                full: true,
            },
            abortSignal,
        );

        for await (const obj of descendantsIter) {
            highlightIds.push(obj.id);
            firstDescendantObjectId ??= obj.id;
        }
    };

    await Promise.all([findParentOfParent(), findParentDescendants()]);

    // If we didn't find the parent object, use the first descendant
    parentObjectId ??= firstDescendantObjectId;

    // If we found objects, return them
    if (highlightIds.length > 0 && parentObjectId !== undefined) {
        return {
            mainObjectId: parentObjectId,
            highlightIds,
        };
    }

    // Fallback: return original object if nothing found
    return single(objectId);
}
